import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, type Article } from '@/mocks/articles';

type SortKey = 'default' | 'newest' | 'hottest' | 'rating';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortKey, setSortKey] = useState<SortKey>('default');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);
  const rawArticles = slug ? getArticlesByCategory(slug) : [];

  const sortArticles = (articles: Article[], key: SortKey): Article[] => {
    const sorted = [...articles];
    switch (key) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
      case 'hottest':
        return sorted.sort((a, b) => b.rating - a.rating);
      case 'rating':
        return sorted.sort((a, b) => b.rating - a.rating);
      default:
        return sorted;
    }
  };

  const articles = sortArticles(rawArticles, sortKey);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex flex-col items-center justify-center px-4">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">抱歉，您访问的分类页面不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 丝瓜视频`,
    description: category.description,
    url: `https://sigua.video/category/${category.slug}`,
    isPartOf: { '@id': 'https://sigua.video/#website' },
  };

  const articleSchemas = articles.map((article, index) => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    url: `https://sigua.video/article/${article.slug}`,
    datePublished: `2024-01-${String(index + 1).padStart(2, '0')}`,
    dateModified: '2024-07-28',
    author: { '@type': 'Person', name: '丝瓜视频编辑' },
    publisher: { '@type': 'Organization', name: '丝瓜视频' },
  }));

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: 'default', label: '全部' },
    { key: 'newest', label: '最新' },
    { key: 'hottest', label: '最热' },
    { key: 'rating', label: '评分最高' },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      {articleSchemas.map((schema, idx) => (
        <script
          key={idx}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}

      <Navbar />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className={`${category.icon} text-xl text-primary-500`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                    {category.name}
                  </h1>
                  <p className="text-foreground-500 text-sm mt-0.5">
                    共 {category.count} 部作品
                  </p>
                </div>
              </div>
              <p className="text-foreground-600 text-sm max-w-2xl">{category.description}</p>
            </div>

            <div className="flex items-center gap-2 mb-6 flex-wrap">
              <span className="text-sm text-foreground-500 font-medium whitespace-nowrap">筛选：</span>
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortKey(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortKey === opt.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200/70 text-foreground-600 hover:bg-background-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {rawArticles.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <div className="w-full h-full bg-background-200 flex items-center justify-center">
                        <i className={`${category.icon} text-4xl text-foreground-300`}></i>
                      </div>
                      <div className="absolute top-2 left-2 flex gap-1.5">
                        {article.isHot && (
                          <span className="px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
                            热门
                          </span>
                        )}
                        {article.isNew && (
                          <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
                            新上线
                          </span>
                        )}
                      </div>
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/60 text-background-50 text-xs font-medium whitespace-nowrap">
                        <i className="ri-star-fill text-accent-400 mr-0.5 text-[10px]"></i>
                        {article.rating}
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="font-heading text-sm md:text-base text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1 mb-1">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-2">
                        {article.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400">{article.episodeCount}</span>
                        <span className="text-xs text-foreground-400">{article.year}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无内容</p>
              </div>
            )}

            {rawArticles.length > 8 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-sm font-medium cursor-pointer">
                  1
                </button>
                <button className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 flex items-center justify-center text-sm font-medium transition-colors cursor-pointer">
                  2
                </button>
                <button className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 flex items-center justify-center text-sm font-medium transition-colors cursor-pointer">
                  3
                </button>
                <button className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 flex items-center justify-center text-sm transition-colors cursor-pointer">
                  <i className="ri-more-line"></i>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}